import React, { Children, isValidElement } from 'react';

const isComponent = (AComponent: React.ComponentType<any>, AnotherComponent: React.ComponentType<any>) =>
  AComponent === AnotherComponent;

// Checks whether `element` is a React element of type `Component` (or one of
// the passed components, if `Component` is an array of React components).
export function isElementOfType<P>(
  element: React.ReactNode | null | undefined,
  Component: React.ComponentType<P> | React.ComponentType<P>[]
): boolean {
  if (element == null || !isValidElement(element) || typeof element.type === 'string') {
    return false;
  }

  const { type } = element;
  const Components = Array.isArray(Component) ? Component : [Component];

  return Components.some(AComponent => typeof type !== 'string' && isComponent(AComponent, type));
}

// Returns all children that are valid elements as an array. Can optionally be
// filtered by passing `predicate`.
export function elementChildren<T extends React.ReactElement<{}>>(
  children: React.ReactNode,
  predicate: (element: T) => boolean = () => true
): T[] {
  return Children.toArray(children).filter(child => isValidElement(child) && predicate(child as T)) as T[];
}

// unnest children out of react fragments and returns a flat array of children
// only unest the top level fragments
export function sanitizeFragments<T extends React.ReactElement<{}>>(children: React.ReactNode): T[] {
  const filteredChildren = Children.toArray(children).filter(x => x !== false);
  return Children.map(filteredChildren, (child: React.ReactElement<any>) => {
    const isChildValid = isValidElement(child);
    if (child && isChildValid && isElementOfType(child, React.Fragment)) {
      const filteredChildren = Children.toArray(child.props.children).filter(x => x !== false);
      return Children.map(filteredChildren, (fragChild: React.ReactElement<any>) => {
        const isFragChildValid = isValidElement(child);
        if (isFragChildValid) {
          return React.cloneElement(fragChild);
        } else {
          return null;
        }
      });
    } else if (isChildValid) {
      return React.cloneElement(child);
    } else return child;
  }) as T[];
}

export const mergeRefs = (...refs) => {
  const filteredRefs = refs.filter(Boolean);
  if (!filteredRefs.length) return null;
  if (filteredRefs.length === 0) return filteredRefs[0];
  return inst => {
    for (const ref of filteredRefs) {
      if (typeof ref === 'function') {
        ref(inst);
      } else if (ref) {
        ref.current = inst;
      }
    }
  };
};
